import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { useAuthenticatedApiClient } from "../hooks/useAuthenticatedApiClient";
import type {
  OrganizationFormInput,
  UserFormInput,
  UserRoleInput,
} from "../types/admin";

// API client will be set by an initializer to avoid hook usage inside store
let apiClient: any = null;
export const setAdminApiClient = (client: any) => {
  apiClient = client;
};

// =============================================================================
// API RESPONSE TYPES (backend contracts)
// =============================================================================

interface OrganizationApiResponse {
  id: number;
  name: string;
  streetAddress?: string;
  streetCity?: string;
  streetStateId?: number;
  streetZip?: string;
  mailingAddress?: string;
  mailingCity?: string;
  mailingStateId?: number;
  mailingZip?: string;
  countyId?: number;
  active?: boolean;
}

interface UserApiResponse {
  id?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  enabled?: boolean;
  isExternal?: boolean;
}

interface UserPermissionApiResponse {
  permissionId: number;
  permissionName: string;
  organizationId: number;
  moduleId: number;
}

interface UserRoleApiResponse {
  id: number; // userRoleId
  roleId: number;
  roleName?: string;
}

interface RoleApiResponse {
  id: number;
  name: string;
}

// =============================================================================
// CONVERSION HELPERS: API ↔ UI
// =============================================================================

const apiToUiOrganization = (
  api: OrganizationApiResponse
): Partial<OrganizationFormInput> => ({
  orgId: api.id,
  orgName: api.name,
  orgStreetAddress: api.streetAddress,
  orgStreetCity: api.streetCity,
  orgStreetZip: api.streetZip,
  orgMailingAddress: api.mailingAddress,
  orgMailingCity: api.mailingCity,
  orgMailingZip: api.mailingZip,
  orgCounty: api.countyId ? String(api.countyId) : "",
  orgActive: api.active ?? true,
});

const uiToApiOrganization = (ui: OrganizationFormInput): any => ({
  name: ui.orgName,
  streetAddress: ui.orgStreetAddress,
  streetCity: ui.orgStreetCity,
  streetStateId: ui.orgStreetState,
  streetZip: ui.orgStreetZip,
  mailingAddress: ui.orgMailingAddress,
  mailingCity: ui.orgMailingCity,
  mailingStateId: ui.orgMailingState,
  mailingZip: ui.orgMailingZip,
  countyId: ui.orgCounty ? Number(ui.orgCounty) : undefined,
});

const apiToUiUser = (api: UserApiResponse): Partial<UserFormInput> => ({
  userId: api.userId || api.id,
  userFirstName: api.firstName,
  userLastName: api.lastName,
  userDisplayName: api.displayName,
  userEmail: api.email,
  userPhoneNumber: api.phoneNumber,
  userEnabled: api.enabled ?? true,
  userIsExternal: api.isExternal ?? false,
});

const uiToApiUser = (ui: UserFormInput): any => ({
  firstName: ui.userFirstName,
  lastName: ui.userLastName,
  displayName: ui.userDisplayName,
  email: ui.userEmail,
  phoneNumber: ui.userPhoneNumber,
  enabled: ui.userEnabled,
  isExternal: ui.userIsExternal,
});

const apiToUiUserRole = (api: UserRoleApiResponse): UserRoleInput => ({
  userRoleId: api.id,
  roleId: api.roleId,
  roleName: api.roleName,
});

interface AdminStoreState {
  organizations: OrganizationFormInput[];
  roles: any[];
  users: UserFormInput[];
  userDetail: {
    [userId: string]: {
      user?: UserFormInput;
      permissions?: UserPermissionApiResponse[];
      roles?: UserRoleInput[];
    };
  };
  searchResults: UserFormInput[];
  mutations: Array<{
    timestamp: string;
    userId: string;
    action: "assignRole" | "removeRole";
    roleId?: number;
    userRoleId?: number;
    status: "pending" | "success" | "error";
    error?: string;
  }>;

  // Organizations
  fetchOrganizations: () => Promise<void>;
  createOrganization: (
    payload: OrganizationFormInput
  ) => Promise<OrganizationFormInput | null>;
  updateOrganization: (
    id: number,
    payload: OrganizationFormInput
  ) => Promise<boolean>;
  disableOrganization: (id: number) => Promise<boolean>;

  // Roles
  fetchRoles: () => Promise<void>;

  // Users
  fetchUsers: () => Promise<void>;
  createUser: (payload: UserFormInput) => Promise<UserFormInput | null>;
  fetchUserById: (userId: string) => Promise<UserFormInput | null>;
  updateUser: (userId: string, payload: UserFormInput) => Promise<boolean>;
  disableUser: (userId: string) => Promise<boolean>;
  fetchUserPermissions: (
    userId: string
  ) => Promise<UserPermissionApiResponse[]>;
  fetchUserRoles: (userId: string) => Promise<UserRoleInput[]>;
  assignUserRole: (
    userId: string,
    roleId: number
  ) => Promise<UserRoleInput | null>;
  removeUserRole: (userId: string, userRoleId: number) => Promise<boolean>;
  searchUsers: (query: string) => Promise<void>;

  // Helpers
  getAdminUserId: (user: UserFormInput | string) => string | null;
  exportMutationLog: () => Blob;

  // Session management
  clearUserDetail: () => void;
}

export const useAdminStore = create<AdminStoreState>()(
  persist(
    (set, get) => ({
      organizations: [],
      roles: [],
      users: [],
      userDetail: {},
      searchResults: [],
      mutations: [],

      fetchOrganizations: async () => {
        if (!apiClient) return;
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient.api("/api/admin/organizations").get();
        });
        set({ organizations: res.content || [] });
      },
      createOrganization: async (payload) => {
        if (!apiClient) return null;
        const apiPayload = uiToApiOrganization(payload);
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient
            .api("/api/admin/organizations")
            .post(apiPayload);
        });
        const org = res.content ? apiToUiOrganization(res.content) : null;
        if (org)
          set((s) => ({
            organizations: [org as OrganizationFormInput, ...s.organizations],
          }));
        return org as OrganizationFormInput | null;
      },
      updateOrganization: async (id, payload) => {
        if (!apiClient) return false;
        const apiPayload = uiToApiOrganization(payload);
        const res = await apiClient.authenticatedRequest(async () => {
          return await (
            apiClient.api(`/api/admin/organizations/${id}`) as any
          ).request("PATCH", apiPayload);
        });
        return res.status < 400;
      },
      disableOrganization: async (id) => {
        if (!apiClient) return false;
        const res = await apiClient.authenticatedRequest(async () => {
          return await (
            apiClient.api(`/api/admin/organizations/${id}`) as any
          ).request("DELETE");
        });
        return res.status < 400;
      },

      fetchRoles: async () => {
        if (!apiClient) return;
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient.api("/api/admin/roles").get();
        });
        const roles = (res.content || []).map((r: RoleApiResponse) => ({
          roleId: r.id,
          roleName: r.name,
        }));
        set({ roles });
      },

      fetchUsers: async () => {
        if (!apiClient) return;
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient.api("/api/admin/users").get();
        });
        set({ users: res.content || [] });
      },
      createUser: async (payload) => {
        if (!apiClient) return null;
        const apiPayload = uiToApiUser(payload);
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient.api("/api/admin/users").post(apiPayload);
        });
        const user = res.content ? apiToUiUser(res.content) : null;
        if (user) set((s) => ({ users: [user as UserFormInput, ...s.users] }));
        return user as UserFormInput | null;
      },
      fetchUserById: async (userId) => {
        if (!apiClient) return null;
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient.api(`/api/admin/users/${userId}`).get();
        });
        const detail = res.content;
        const user = detail ? apiToUiUser(detail) : null;

        // Extract permissions from userPermissions array in response
        const permissions: UserPermissionApiResponse[] = Array.isArray(
          detail?.userPermissions
        )
          ? detail.userPermissions.map((p: any) => ({
              permissionId: p.permissionId,
              permissionName: p.permissionName,
              organizationId: p.organizationId,
              moduleId: p.moduleId,
            }))
          : [];

        // Extract roles from userRoles array in response
        const roles: UserRoleInput[] = Array.isArray(detail?.userRoles)
          ? detail.userRoles.map((ur: any) => ({
              userRoleId: ur.id,
              roleId: ur.role?.id,
              roleName: ur.role?.name,
              organizationName: ur.organization?.name,
              organizationId: ur.organization?.id,
            }))
          : [];

        if (user)
          set((s) => ({
            userDetail: {
              ...s.userDetail,
              [userId]: {
                user: user as UserFormInput,
                permissions,
                roles,
              },
            },
          }));
        return user as UserFormInput | null;
      },
      updateUser: async (userId, payload) => {
        if (!apiClient) return false;
        const apiPayload = uiToApiUser(payload);
        const res = await apiClient.authenticatedRequest(async () => {
          return await (
            apiClient.api(`/api/admin/users/${userId}`) as any
          ).request("PATCH", apiPayload);
        });
        return res.status < 400;
      },
      disableUser: async (userId) => {
        if (!apiClient) return false;
        const res = await apiClient.authenticatedRequest(async () => {
          return await (
            apiClient.api(`/api/admin/users/${userId}`) as any
          ).request("DELETE");
        });
        return res.status < 400;
      },
      fetchUserPermissions: async (userId) => {
        if (!apiClient) return [];
        try {
          const res = await apiClient.authenticatedRequest(async () => {
            return await apiClient
              .api(`/api/admin/users/${userId}/permissions`)
              .get();
          });
          // Response is array of { organizationId, userPermissions: string[] }
          // Flatten to permission objects matching UserPermissionApiResponse
          const permData = Array.isArray(res.content) ? res.content : [];
          const perms: UserPermissionApiResponse[] = [];
          permData.forEach((org: any) => {
            if (Array.isArray(org.userPermissions)) {
              org.userPermissions.forEach((permName: string, idx: number) => {
                perms.push({
                  permissionId: idx,
                  permissionName: permName,
                  organizationId: org.organizationId,
                  moduleId: 0, // Not available from this endpoint
                });
              });
            }
          });
          set((s) => ({
            userDetail: {
              ...s.userDetail,
              [userId]: {
                ...(s.userDetail[userId] || {}),
                permissions: perms,
              },
            },
          }));
          return perms;
        } catch (err) {
          console.error(`Error fetching user permissions for ${userId}:`, err);
          return [];
        }
      },
      fetchUserRoles: async (userId) => {
        if (!apiClient) return [];
        try {
          const res = await apiClient.authenticatedRequest(async () => {
            return await apiClient.api(`/api/admin/users/${userId}`).get();
          });
          const detail = res.content || {};
          // Response has userRoles array with nested role object and organization
          let roles: UserRoleInput[] = [];
          if (Array.isArray(detail.userRoles)) {
            roles = detail.userRoles.map((ur: any) => ({
              userRoleId: ur.id,
              roleId: ur.role?.id,
              roleName: ur.role?.name,
              organizationName: ur.organization?.name,
              organizationId: ur.organization?.id,
            }));
          }
          set((s) => ({
            userDetail: {
              ...s.userDetail,
              [userId]: { ...(s.userDetail[userId] || {}), roles },
            },
          }));
          return roles;
        } catch (err) {
          console.error(`Error fetching user roles for ${userId}:`, err);
          return [];
        }
      },
      assignUserRole: async (userId, roleId) => {
        if (!apiClient) return null;
        set((s) => ({
          mutations: [
            ...s.mutations,
            {
              timestamp: new Date().toISOString(),
              userId,
              action: "assignRole",
              roleId,
              status: "pending",
            },
          ],
        }));
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient
            .api(`/api/admin/users/${userId}/roles`)
            .post({ roleId });
        });
        const ok = res.status < 400;
        set((s) => ({
          mutations: s.mutations.map((m) =>
            m.userId === userId &&
            m.action === "assignRole" &&
            m.roleId === roleId &&
            m.status === "pending"
              ? {
                  ...m,
                  status: ok ? "success" : "error",
                  error: ok
                    ? undefined
                    : res.content?.detail || `HTTP ${res.status}`,
                }
              : m
          ),
        }));
        if (!ok) return null;
        const created: UserRoleInput = res.content
          ? apiToUiUserRole(res.content)
          : { roleId };
        await get().fetchUserRoles(userId);
        return created || null;
      },
      removeUserRole: async (userId, userRoleId) => {
        if (!apiClient) return false;
        set((s) => ({
          mutations: [
            ...s.mutations,
            {
              timestamp: new Date().toISOString(),
              userId,
              action: "removeRole",
              userRoleId,
              status: "pending",
            },
          ],
        }));
        const res = await apiClient.authenticatedRequest(async () => {
          return await (
            apiClient.api(
              `/api/admin/users/${userId}/roles/${userRoleId}`
            ) as any
          ).request("DELETE");
        });
        const ok = res.status < 400;
        set((s) => ({
          mutations: s.mutations.map((m) =>
            m.userId === userId &&
            m.action === "removeRole" &&
            m.userRoleId === userRoleId &&
            m.status === "pending"
              ? {
                  ...m,
                  status: ok ? "success" : "error",
                  error: ok
                    ? undefined
                    : res.content?.detail || `HTTP ${res.status}`,
                }
              : m
          ),
        }));
        if (ok) await get().fetchUserRoles(userId);
        return ok;
      },
      searchUsers: async (query) => {
        if (!apiClient) return;
        const res = await apiClient.authenticatedRequest(async () => {
          return await apiClient
            .api(`/api/admin/users/search`)
            .get({ params: { q: query } });
        });
        set({ searchResults: res.content || [] });
      },

      getAdminUserId: (userOrId) => {
        if (!userOrId) return null;
        if (typeof userOrId === "string") return userOrId;
        return userOrId.userId ?? null;
      },
      exportMutationLog: () => {
        const log = get().mutations;
        return new Blob([JSON.stringify(log, null, 2)], {
          type: "application/json",
        });
      },

      clearUserDetail: () => {
        set({ userDetail: {} });
      },
    }),
    {
      name: "flehs-admin-store",
      storage: createJSONStorage(() => localStorage),
      // Only persist userDetail (for RBAC), not the full admin state
      partialize: (state) => ({ userDetail: state.userDetail }),
    }
  )
);

// Helper to wire the API client from a React component once
export const useInitAdminStoreApiClient = () => {
  const client = useAuthenticatedApiClient("flehsApi");
  // one-time set
  setAdminApiClient(client);
};
